import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import Modal from "@/components/Modal";
import { Btn, Input, Textarea, Select, Field } from "@/components/common";
import { api, apiError } from "@/lib/api";

const TYPES = [
  { v: "location", l: "Physical location / area" },
  { v: "business", l: "Business" },
  { v: "organization", l: "Organization" },
  { v: "person", l: "Person" },
  { v: "event", l: "Event" },
  { v: "website", l: "Website" },
  { v: "community", l: "Online community / group" },
  { v: "directory", l: "Directory" },
];
const BOUNDARIES = ["radius", "polygon", "named_area", "directory", "community", "domain", "urls"];
const REL_TYPES = ["located_beside", "works_for", "owns", "member_of", "introduced_by", "supplies", "participates_in", "collaborates_with", "links_to"];

const STEPS = ["Name", "Type", "Location / reference", "Surroundings", "Expansion limits", "Purpose", "Review"];

export default function OriginWizard({ open, onClose, workspaceId, onCreated }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: "", type: "location", purpose: "", address: "", lat: "", lng: "", url: "",
    boundary_kind: "radius", radius_m: 1000, named_area: "",
    allowed_relationship_types: [], expansion_depth: 1,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const geo = ["location", "business", "organization", "event", "person"].includes(f.type);
  const digital = ["website", "community", "directory"].includes(f.type);

  const next = () => {
    if (step === 0 && !f.name.trim()) return toast.error("Please name the origin");
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        ...f,
        lat: f.lat === "" ? null : parseFloat(f.lat),
        lng: f.lng === "" ? null : parseFloat(f.lng),
        radius_m: parseFloat(f.radius_m) || 1000,
        expansion_depth: parseInt(f.expansion_depth) || 1,
      };
      const { data } = await api.post(`/origins?workspace_id=${workspaceId}`, body);
      toast.success("Origin created");
      onCreated(data);
      onClose();
      setStep(0);
      setF({ name: "", type: "location", purpose: "", address: "", lat: "", lng: "", url: "", boundary_kind: "radius", radius_m: 1000, named_area: "", allowed_relationship_types: [], expansion_depth: 1 });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const toggleRel = (r) => set("allowed_relationship_types", f.allowed_relationship_types.includes(r) ? f.allowed_relationship_types.filter((x) => x !== r) : [...f.allowed_relationship_types, r]);

  return (
    <Modal open={open} onClose={onClose} title="New Spider Contact Origin" width={620} testid="origin-wizard">
      {/* stepper */}
      <div className="flex items-center gap-1 mb-6 flex-wrap">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-1 text-xs font-mono"
            style={{ color: i === step ? "var(--si-silver)" : i < step ? "var(--si-teal-light)" : "var(--si-text-2)" }}>
            <span style={{ width: 18, height: 18, borderRadius: 2, border: `1px solid ${i <= step ? "var(--si-teal-light)" : "var(--si-border)"}`, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              {i < step ? <Check size={11} /> : i + 1}
            </span>
            {i < STEPS.length - 1 && <span style={{ color: "var(--si-border)" }}>—</span>}
          </div>
        ))}
      </div>
      <div className="si-label mb-4">Step {step + 1} / {STEPS.length} · {STEPS[step]}</div>

      {step === 0 && <Field label="Origin name"><Input data-testid="wiz-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Riverton Village Office" autoFocus /></Field>}

      {step === 1 && (
        <Field label="Origin type">
          <div className="grid grid-cols-2 gap-2">
            {TYPES.map((t) => (
              <button key={t.v} data-testid={`wiz-type-${t.v}`} onClick={() => set("type", t.v)}
                className="px-3 py-2.5 text-sm text-left transition-colors duration-200"
                style={{ border: `1px solid ${f.type === t.v ? "var(--si-teal-light)" : "var(--si-border)"}`, color: f.type === t.v ? "var(--si-text)" : "var(--si-text-2)", background: f.type === t.v ? "var(--si-surface-hover)" : "transparent", borderRadius: 2 }}>
                {t.l}
              </button>
            ))}
          </div>
        </Field>
      )}

      {step === 2 && (
        <div>
          {geo && <>
            <Field label="Address"><Input data-testid="wiz-address" value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="Street, city" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Latitude" hint="Optional"><Input data-testid="wiz-lat" value={f.lat} onChange={(e) => set("lat", e.target.value)} placeholder="51.5074" /></Field>
              <Field label="Longitude" hint="Optional"><Input data-testid="wiz-lng" value={f.lng} onChange={(e) => set("lng", e.target.value)} placeholder="-0.1278" /></Field>
            </div>
          </>}
          {digital && <Field label="URL / reference"><Input data-testid="wiz-url" value={f.url} onChange={(e) => set("url", e.target.value)} placeholder="https://..." /></Field>}
          {!geo && !digital && <div className="text-sm" style={{ color: "var(--si-text-2)" }}>This origin type needs no location. Continue.</div>}
        </div>
      )}

      {step === 3 && (
        <div>
          <Field label="Boundary / scope kind">
            <Select data-testid="wiz-boundary" value={f.boundary_kind} onChange={(e) => set("boundary_kind", e.target.value)}>
              {BOUNDARIES.map((b) => <option key={b} value={b}>{b.replace(/_/g, " ")}</option>)}
            </Select>
          </Field>
          {f.boundary_kind === "radius" && <Field label="Radius (meters)"><Input data-testid="wiz-radius" type="number" value={f.radius_m} onChange={(e) => set("radius_m", e.target.value)} /></Field>}
          {["named_area", "directory", "community"].includes(f.boundary_kind) && <Field label="Named area / directory / community"><Input value={f.named_area} onChange={(e) => set("named_area", e.target.value)} placeholder="Name" /></Field>}
          <Field label="Allowed relationship types" hint="Which documented connections define this network's surroundings.">
            <div className="flex flex-wrap gap-2">
              {REL_TYPES.map((r) => (
                <button key={r} onClick={() => toggleRel(r)} className="px-2 py-1 text-xs font-mono transition-colors duration-200"
                  style={{ border: `1px solid ${f.allowed_relationship_types.includes(r) ? "var(--si-teal-light)" : "var(--si-border)"}`, color: f.allowed_relationship_types.includes(r) ? "var(--si-text)" : "var(--si-text-2)", borderRadius: 2 }}>
                  {r.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </Field>
        </div>
      )}

      {step === 4 && (
        <Field label="Expansion depth" hint="How many steps outward from the origin may be expanded. Larger networks load bounded neighborhoods with explicit expansion controls.">
          <Input data-testid="wiz-depth" type="number" min={1} max={5} value={f.expansion_depth} onChange={(e) => set("expansion_depth", e.target.value)} />
        </Field>
      )}

      {step === 5 && <Field label="Purpose"><Textarea data-testid="wiz-purpose" value={f.purpose} onChange={(e) => set("purpose", e.target.value)} placeholder="Why are you mapping this network?" /></Field>}

      {step === 6 && (
        <div className="space-y-2 text-sm font-mono p-4" style={{ background: "var(--si-bg)", border: "1px solid var(--si-border)", borderRadius: 2 }}>
          <div><span className="si-label">name</span> <span style={{ color: "var(--si-text)" }}>{f.name}</span></div>
          <div><span className="si-label">type</span> <span style={{ color: "var(--si-text)" }}>{f.type}</span></div>
          {geo && <div><span className="si-label">location</span> <span style={{ color: "var(--si-text)" }}>{f.address || "—"} {f.lat && `(${f.lat}, ${f.lng})`}</span></div>}
          {digital && <div><span className="si-label">reference</span> <span style={{ color: "var(--si-text)" }}>{f.url || "—"}</span></div>}
          <div><span className="si-label">boundary</span> <span style={{ color: "var(--si-text)" }}>{f.boundary_kind}{f.boundary_kind === "radius" ? ` · ${f.radius_m}m` : ""}</span></div>
          <div><span className="si-label">depth</span> <span style={{ color: "var(--si-text)" }}>{f.expansion_depth}</span></div>
          <div><span className="si-label">purpose</span> <span style={{ color: "var(--si-text)" }}>{f.purpose || "—"}</span></div>
        </div>
      )}

      <div className="flex justify-between mt-6">
        <Btn variant="ghost" onClick={back} disabled={step === 0}><ArrowLeft size={15} /> Back</Btn>
        {step < STEPS.length - 1
          ? <Btn variant="secondary" onClick={next} data-testid="wiz-next">Next <ArrowRight size={15} /></Btn>
          : <Btn variant="primary" onClick={save} disabled={saving} data-testid="wiz-save">{saving ? "Saving…" : "Save & open network"}</Btn>}
      </div>
    </Modal>
  );
}
